import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  getCurrentUser,
  getMyWorkQueue,
  updateTask,
} from "../api";

export default function MyWorkQueue() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  const [loading, setLoading] = useState(true);

  const [message, setMessage] = useState("");

  const [autopilotRunning, setAutopilotRunning] = useState(false);

  const [search, setSearch] = useState("");

  const [queue, setQueue] = useState({
    user: {},
    summary: {
      assigned_clients: 0,
      open_tasks: 0,
      overdue_tasks: 0,
      open_disputes: 0,
    },
    assigned_clients: [],
    tasks: [],
    overdue_tasks: [],
    upcoming_tasks: [],
    disputes: [],
  });

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setMessage("");

    try {
      const data = await getMyWorkQueue();

      setQueue({
        user: data.user || {},
        summary: data.summary || {},
        assigned_clients:
          data.assigned_clients || [],
        tasks: data.tasks || [],
        overdue_tasks:
          data.overdue_tasks || [],
        upcoming_tasks:
          data.upcoming_tasks || [],
        disputes:
          data.disputes || [],
      });
    } catch (err) {
      setMessage(
        err.message ||
          "Unable to load your work queue."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  async function handleCompleteTask(task) {
    const confirmed = window.confirm(
      `Mark "${task.title || "this task"}" as completed?`
    );

    if (!confirmed) return;

    try {
      setMessage("");

      await updateTask(task.id, {
        title: task.title || "",
        description: task.description || "",
        status: "completed",
        due_date: task.due_date || "",
      });

      await loadQueue();
    } catch (error) {
      setMessage(error?.message || "Unable to complete task.");
    }
  }

    async function handleRunAutoPilot() {
    const assignedClients = Array.isArray(queue.assigned_clients)
      ? queue.assigned_clients
      : [];

    if (assignedClients.length === 0) {
      setMessage("There are no assigned clients for AutoPilot to process.");
      return;
    }

    const confirmed = window.confirm(
      `Run AutoPilot for ${assignedClients.length} assigned client${
        assignedClients.length === 1 ? "" : "s"
      }?`
    );

    if (!confirmed) return;

    const token =
      localStorage.getItem("access_token") ||
      localStorage.getItem("token");

    if (!token) {
      setMessage("Your login session is missing. Please log out and log back in.");
      return;
    }

    setAutopilotRunning(true);
    setMessage("");

    let completedCount = 0;
    const failedClients = [];

    try {
      for (const client of assignedClients) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE || "http://127.0.0.1:3001"}/autopilot/generate-workflow/${client.id}`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          const data = await response.json().catch(() => ({}));

          if (!response.ok) {
            throw new Error(
              data.detail ||
                data.message ||
                `AutoPilot failed with status ${response.status}.`
            );
          }

          completedCount += 1;
        } catch (error) {
          failedClients.push({
            name: client.full_name || `Client ${client.id}`,
            error: error.message,
          });
        }
      }

      await loadQueue();

      if (failedClients.length === 0) {
        setMessage(
          `AutoPilot completed successfully for ${completedCount} client${
            completedCount === 1 ? "" : "s"
          }.`
        );
      } else {
        const failureSummary = failedClients
          .map((item) => `${item.name}: ${item.error}`)
          .join(" | ");

        setMessage(
          `AutoPilot completed for ${completedCount} client${
            completedCount === 1 ? "" : "s"
          }. Failed: ${failureSummary}`
        );
      }
    } catch (error) {
      setMessage(
        error?.message || "Unable to run the AutoPilot workflow."
      );
    } finally {
      setAutopilotRunning(false);
    }
  }

  const clients = useMemo(() => {
    if (!search) return queue.assigned_clients;

    return queue.assigned_clients.filter((c) =>
      (
        c.full_name ||
        `${c.first_name} ${c.last_name}`
      )
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [queue.assigned_clients, search]);

  const tasks = useMemo(() => {
    if (!search) return queue.tasks;

    return queue.tasks.filter((t) =>
      (
        t.title +
        " " +
        t.description +
        " " +
        t.client_name
      )
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [queue.tasks, search]);

  const disputes = useMemo(() => {
    if (!search) return queue.disputes;

    return queue.disputes.filter((d) =>
      (
        d.creditor +
        " " +
        d.client_name +
        " " +
        d.reason
      )
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [queue.disputes, search]);

  const summary = queue.summary || {
    assigned_clients: 0,
    open_tasks: 0,
    overdue_tasks: 0,
    open_disputes: 0,
  };

  const overdueTasks = Array.isArray(queue.overdue_tasks)
    ? queue.overdue_tasks
    : [];

  const upcomingTasks = Array.isArray(queue.upcoming_tasks)
    ? queue.upcoming_tasks
    : [];

  return (
    <div
      style={{
        padding: 30,
        background: "#f4f7fb",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 30,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              color: "#64748b",
              fontWeight: 700,
              fontSize: 18,
            }}
          >
            Integrity Suite CRM
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: 60,
              fontWeight: 900,
              color: "#0f172a",
            }}
          >
            My Work Queue
          </h1>

          <div
            style={{
              color: "#64748b",
              marginTop: 8,
              fontSize: 17,
            }}
          >
            Welcome back{" "}
            <strong>
              {currentUser?.full_name ||
                currentUser?.email}
            </strong>
          </div>
        </div>

        <button
          onClick={loadQueue}
          style={{
            background: "#D4AF37",
            color: "#111111",
            border: "none",
            borderRadius: 14,
            padding: "15px 24px",
            fontSize: 16,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {loading
            ? "Refreshing..."
            : "Refresh Queue"}
        </button>
      </div>

      {message && (
        <div
          style={{
            background: "#fee2e2",
            color: "#991b1b",
            padding: 18,
            borderRadius: 16,
            marginBottom: 20,
            fontWeight: 700,
          }}
        >
          {message}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit,minmax(220px,1fr))",
          gap: 18,
          marginBottom: 25,
        }}
      >
        <SummaryCard
          title="Assigned Clients"
          value={
            queue.summary.assigned_clients
          }
        />

        <SummaryCard
          title="Open Tasks"
          value={queue.summary.open_tasks}
        />

        <SummaryCard
          title="Overdue Tasks"
          value={
            queue.summary.overdue_tasks
          }
          danger
        />

        <SummaryCard
          title="Open Disputes"
          value={
            queue.summary.open_disputes
          }
        />
      </div>

      <input
        value={search}
        onChange={(e) =>
          setSearch(e.target.value)
        }
        placeholder="Search clients, tasks or disputes..."
        style={{
          width: "100%",
          padding: 16,
          borderRadius: 14,
          border: "1px solid #cbd5e1",
          marginBottom: 25,
          fontSize: 16,
        }}
      />
       
            <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "24px",
          marginBottom: "30px",
        }}
      >
        {/* ================= CLIENTS ================= */}

        <div
          style={{
            background: "white",
            borderRadius: "22px",
            padding: "24px",
            boxShadow: "0 8px 24px rgba(0,0,0,.06)",
          }}
        >
          <h2
            style={{
              marginTop: 0,
              marginBottom: "20px",
              fontSize: "30px",
              fontWeight: "800",
            }}
          >
            My Clients
          </h2>

          {clients.length === 0 ? (
            <div
              style={{
                color: "#64748b",
              }}
            >
              No assigned clients.
            </div>
          ) : (
            clients.map((client) => (
              <div
                key={client.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "18px",
                  padding: "18px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    fontWeight: "700",
                    fontSize: "22px",
                  }}
                >
                  {client.full_name}
                </div>

                <div
                  style={{
                    color: "#64748b",
                    marginTop: "6px",
                  }}
                >
                  {client.email}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "18px",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() =>
                      navigate(`/clients/${client.id}`)
                    }
                    style={blueButton}
                  >
                    Open Client
                  </button>

                  <button
                    onClick={() =>
                      navigate(
                        `/letters?client_id=${client.id}`
                      )
                    }
                    style={grayButton}
                  >
                    Generate Letter
                  </button>

                  <button
                    onClick={() =>
                      navigate(
                        `/credit-reports?client_id=${client.id}`
                      )
                    }
                    style={grayButton}
                  >
                    Credit Report
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ================= TASKS ================= */}

        <div
          style={{
            background: "white",
            borderRadius: "22px",
            padding: "24px",
            boxShadow: "0 8px 24px rgba(0,0,0,.06)",
          }}
        >
          <h2
            style={{
              marginTop: 0,
              marginBottom: "20px",
              fontSize: "30px",
              fontWeight: "800",
            }}
          >
            My Tasks
          </h2>

          {tasks.length === 0 ? (
            <div
              style={{
                color: "#64748b",
              }}
            >
              No assigned tasks.
            </div>
          ) : (
            tasks.map((task) => (
              <div
                key={task.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "18px",
                  padding: "18px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    fontWeight: "700",
                    fontSize: "21px",
                  }}
                >
                  {task.title}
                </div>

                <div
                  style={{
                    color: "#64748b",
                    marginTop: "8px",
                  }}
                >
                  {task.description}
                </div>

                <div
                  style={{
                    marginTop: "12px",
                    fontWeight: "700",
                    color: "#A67C00",
                  }}
                >
                  Due: {task.due_date || "None"}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "18px",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() =>
                      navigate("/notes-tasks")
                    }
                    style={blueButton}
                  >
                    Open Task
                  </button>

                  <button
                    type="button"
                    onClick={() => handleCompleteTask(task)}
                    style={grayButton}
                  >
                    Complete
                  </button>                  

                </div>
              </div>
            ))
          )}
        </div>
      </div>

            <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "24px",
          marginBottom: "30px",
        }}
      >
        {/* ================= DISPUTES ================= */}

        <div
          style={{
            background: "white",
            borderRadius: "22px",
            padding: "24px",
            boxShadow: "0 8px 24px rgba(0,0,0,.06)",
          }}
        >
          <h2
            style={{
              marginTop: 0,
              marginBottom: "20px",
              fontSize: "30px",
              fontWeight: "800",
            }}
          >
            Assigned Disputes
          </h2>

          {disputes.length === 0 ? (
            <div
              style={{
                color: "#64748b",
              }}
            >
              No assigned disputes.
            </div>
          ) : (
            disputes.map((dispute) => (
              <div
                key={dispute.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "18px",
                  padding: "18px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    fontWeight: "700",
                    fontSize: "21px",
                  }}
                >
                  {dispute.creditor || "Unknown Creditor"}
                </div>

                <div
                  style={{
                    color: "#64748b",
                    marginTop: "8px",
                  }}
                >
                  Client: {dispute.client_name || "Unknown Client"}
                </div>

                <div
                  style={{
                    color: "#64748b",
                    marginTop: "6px",
                  }}
                >
                  Account: {dispute.account_number || "N/A"}
                </div>

                <div
                  style={{
                    color: "#64748b",
                    marginTop: "6px",
                  }}
                >
                  Round: {dispute.round || 1}
                </div>

                <div
                  style={{
                    marginTop: "12px",
                    fontWeight: "700",
                    color: "#2563eb",
                  }}
                >
                  Status: {dispute.status || "open"}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "18px",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() => navigate("/disputes")}
                    style={blueButton}
                  >
                    Open Dispute
                  </button>

                  <button
                    onClick={() =>
                      navigate(
                        `/letters?client_id=${dispute.client_id}`
                      )
                    }
                    style={grayButton}
                  >
                    Generate Letter
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ================= OVERDUE TASKS ================= */}

        <div
          style={{
            background: "white",
            borderRadius: "22px",
            padding: "24px",
            boxShadow: "0 8px 24px rgba(0,0,0,.06)",
          }}
        >
          <h2
            style={{
              marginTop: 0,
              marginBottom: "20px",
              fontSize: "30px",
              fontWeight: "800",
              color: "#991b1b",
            }}
          >
            Overdue Tasks
          </h2>

          {overdueTasks.length === 0 ? (
            <div
              style={{
                color: "#64748b",
              }}
            >
              No overdue tasks.
            </div>
          ) : (
            overdueTasks.map((task) => (
              <div
                key={task.id}
                style={{
                  border: "1px solid #fecaca",
                  background: "#fff7f7",
                  borderRadius: "18px",
                  padding: "18px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    fontWeight: "700",
                    fontSize: "21px",
                    color: "#991b1b",
                  }}
                >
                  {task.title}
                </div>

                <div
                  style={{
                    color: "#64748b",
                    marginTop: "8px",
                  }}
                >
                  {task.description}
                </div>

                <div
                  style={{
                    color: "#991b1b",
                    marginTop: "12px",
                    fontWeight: "700",
                  }}
                >
                  Due: {task.due_date || "No date"}
                </div>

                <button
                  onClick={() => navigate("/notes-tasks")}
                  style={{
                    ...blueButton,
                    marginTop: "16px",
                    background: "#dc2626",
                  }}
                >
                  Handle Now
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ================= UPCOMING TASKS ================= */}

      <div
        style={{
          background: "white",
          borderRadius: "22px",
          padding: "24px",
          boxShadow: "0 8px 24px rgba(0,0,0,.06)",
          marginBottom: "30px",
        }}
      >
        <h2
          style={{
            marginTop: 0,
            marginBottom: "20px",
            fontSize: "30px",
            fontWeight: "800",
          }}
        >
          Upcoming Tasks
        </h2>

        {upcomingTasks.length === 0 ? (
          <div style={{ color: "#64748b" }}>
            No upcoming tasks.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit,minmax(280px,1fr))",
              gap: "16px",
            }}
          >
            {upcomingTasks.map((task) => (
              <div
                key={task.id}
                style={{
                  border: "1px solid #dbeafe",
                  background: "#f8fbff",
                  borderRadius: "18px",
                  padding: "18px",
                }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                  }}
                >
                  {task.title}
                </div>

                <div
                  style={{
                    color: "#64748b",
                    marginTop: "8px",
                  }}
                >
                  {task.client_name || "General Task"}
                </div>

                <div
                  style={{
                    color: "#2563eb",
                    fontWeight: "700",
                    marginTop: "12px",
                  }}
                >
                  Due: {task.due_date || "No date"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= AI AUTOPILOT ================= */}

      <div
        style={{
          background:
            "linear-gradient(135deg,#0f172a 0%,#1e3a8a 100%)",
          color: "white",
          borderRadius: "24px",
          padding: "28px",
          boxShadow: "0 12px 30px rgba(15,23,42,.18)",
          marginBottom: "30px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "20px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "800",
                letterSpacing: "1.5px",
                color: "#93c5fd",
                marginBottom: "10px",
              }}
            >
              INTEGRITY AI
            </div>

            <h2
              style={{
                margin: 0,
                fontSize: "34px",
                fontWeight: "800",
              }}
            >
              AutoPilot Work Recommendations
            </h2>

            <div
              style={{
                color: "#dbeafe",
                fontSize: "17px",
                lineHeight: 1.6,
                marginTop: "12px",
                maxWidth: "760px",
              }}
            >
              Review overdue tasks first, then process assigned
              disputes and prepare the next appropriate dispute round
              for each client.
            </div>
          </div>

          <button
            type="button"
            onClick={handleRunAutoPilot}
            disabled={autopilotRunning}
            style={{
              border: "none",
              background: autopilotRunning
                ? "#64748b"
                : "#22c55e",
              color: "white",
              padding: "16px 24px",
              borderRadius: "16px",
              fontSize: "17px",
              fontWeight: "800",
              cursor: autopilotRunning
                ? "not-allowed"
                : "pointer",
              opacity: autopilotRunning ? 0.8 : 1,
            }}
          >
            {autopilotRunning
              ? "Running AutoPilot..."
              : "Run AutoPilot"}
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit,minmax(180px,1fr))",
            gap: "14px",
            marginTop: "24px",
          }}
        >
          <AutoPilotCard
            label="Clients to Review"
            value={summary.assigned_clients}
          />

          <AutoPilotCard
            label="Tasks to Complete"
            value={summary.open_tasks}
          />

          <AutoPilotCard
            label="Disputes to Monitor"
            value={summary.open_disputes}
          />

          <AutoPilotCard
            label="Urgent Items"
            value={summary.overdue_tasks}
          />
        </div>
      </div>

    </div>
  );
}

/* =========================================================
   REUSABLE COMPONENTS
========================================================= */

function SummaryCard({ title, value, danger = false }) {
  return (
    <div
      style={{
        background: "white",
        borderRadius: "20px",
        padding: "22px",
        boxShadow: "0 8px 24px rgba(0,0,0,.06)",
        border: danger
          ? "2px solid #fecaca"
          : "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: "16px",
          fontWeight: "700",
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: "12px",
          fontSize: "54px",
          fontWeight: "800",
          color: danger ? "#dc2626" : "#0f172a",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function AutoPilotCard({ label, value }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,.08)",
        borderRadius: "18px",
        padding: "20px",
        border: "1px solid rgba(255,255,255,.12)",
      }}
    >
      <div
        style={{
          color: "#bfdbfe",
          fontSize: "15px",
          fontWeight: "700",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: "10px",
          fontSize: "42px",
          fontWeight: "800",
          color: "white",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* =========================================================
   BUTTON STYLES
========================================================= */

const blueButton = {
  border: "none",
  background: "#2563eb",
  color: "white",
  padding: "12px 18px",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: "700",
  fontSize: "15px",
};

const grayButton = {
  border: "1px solid #d1d5db",
  background: "white",
  color: "#111827",
  padding: "12px 18px",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: "700",
  fontSize: "15px",
};
